import React from 'react'
import axios from 'axios'
import { APIProvider, AdvancedMarker, InfoWindow, Map as GoogleMap, Pin } from '@vis.gl/react-google-maps'
import { MdClose, MdPersonPinCircle } from 'react-icons/md'
import { addDays, format } from 'date-fns'
import { baseUrl } from '../../assets/Animations'
import { getRequestHeaders } from '../../api'
import { createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime } from '../../Calendar/calendarFunctions'
import './teacher-map-popup.css'

const GOOGLE_MAPS_API_KEY = 'AIzaSyBLfMu_7gTWuHw6C3kOqLB8uBUjP9eNLF8'
const DEFAULT_MAP_CENTER = { lat: 40.015, lng: -105.2705 }
const GOOGLE_MAP_ID = '3324feda48bc0f19'

const normalizeInstrument = (instrument) => {
    if (!instrument) return { key: null, label: null }
    const label = instrument.parentInstrument && instrument.parentInstrument !== 'Skill'
        ? instrument.parentInstrument
        : instrument.name
    const trimmed = label?.trim()
    return trimmed ? { key: trimmed.toLowerCase(), label: trimmed } : { key: null, label: null }
}

const isValidLatLng = (location) =>
    Boolean(location) && Number.isFinite(location.lat) && Number.isFinite(location.lng)

const getTeacherInstruments = (teacher) =>
    Array.isArray(teacher?.InstrumentList)
        ? teacher.InstrumentList.map((inst) => normalizeInstrument(inst).label).filter(Boolean)
        : []

const getStudentDisplayName = (student) =>
    String(
        student?.studentName ||
        student?.StudentName ||
        student?.firstName ||
        student?.name ||
        'Student'
    ).trim()

const getLessonSlotLabel = (event) => {
    if (!event) return null
    if (event.day && event.time) {
        return `${event.day} ${event.time}`
    }
    if (!event.dateTime) return null
    const date = new Date(event.dateTime)
    if (Number.isNaN(date.getTime())) return null
    return `${format(date, 'EEEE')} ${format(date, 'h:mm a')}`
}

const TeacherMapPopup = ({ modalInfo, familyProfile = null, onClose }) => {
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState('')
    const [markers, setMarkers] = React.useState([])
    const [selectedMarkerId, setSelectedMarkerId] = React.useState(null)
    const [pinnedTeacherId, setPinnedTeacherId] = React.useState(null)
    const [mapsReady, setMapsReady] = React.useState(false)
    const [selectedTeacherStudents, setSelectedTeacherStudents] = React.useState([])
    const [selectedTeacherLoading, setSelectedTeacherLoading] = React.useState(false)

    const instrumentFilters = React.useMemo(() => {
        const unique = {}
        ;(modalInfo?.studentList || []).forEach((student) => {
            ;(student?.instrumentList || []).forEach((instrument) => {
                const normalized = normalizeInstrument(instrument)
                if (normalized.key && !unique[normalized.key]) {
                    unique[normalized.key] = normalized.label
                }
            })
        })
        return Object.entries(unique).map(([key, label]) => ({ key, label }))
    }, [modalInfo?.studentList])

    const schoolFilter = React.useMemo(
        () => String(modalInfo?.whichSchool || '').trim().toLowerCase(),
        [modalInfo?.whichSchool]
    )

    const familyAddressLabel = React.useMemo(() => {
        const address = familyProfile?.address || {}
        const zip = address?.zipcode ? ` ${address.zipcode}` : ''
        return [
            address?.addressLine1,
            address?.addressLine2,
            [address?.city, address?.state].filter(Boolean).join(', ')
        ]
            .filter(Boolean)
            .join(', ')
            .concat(zip)
            .trim()
    }, [familyProfile])

    React.useEffect(() => {
        if (mapsReady) return undefined

        const checkIfReady = () => {
            if (window.google?.maps?.Geocoder) {
                setMapsReady(true)
                return true
            }
            return false
        }

        if (checkIfReady()) {
            return undefined
        }

        const intervalId = window.setInterval(() => {
            if (checkIfReady()) {
                window.clearInterval(intervalId)
            }
        }, 250)

        return () => window.clearInterval(intervalId)
    }, [mapsReady])

    const geocodeAddress = React.useCallback(async (addressString) => {
        if (!addressString || !addressString.trim()) return null
        if (!window.google?.maps?.Geocoder) return null
        try {
            const geocoder = new window.google.maps.Geocoder()
            const response = await geocoder.geocode({ address: addressString })
            const firstResult = Array.isArray(response?.results) ? response.results[0] : null
            return firstResult?.geometry?.location?.toJSON?.() || null
        } catch (err) {
            console.log(err)
            return null
        }
    }, [])

    React.useEffect(() => {
        if (!mapsReady) return undefined

        let isActive = true

        const loadMapData = async () => {
            setLoading(true)
            setError('')
            try {
                const teachersResponse = await axios.get(
                    `${baseUrl}/teachersbyschool/${schoolFilter}`,
                    getRequestHeaders()
                )
                const allTeachers = Array.isArray(teachersResponse?.data) ? teachersResponse.data : []

                const filteredTeachers = allTeachers
                    .filter((teacher) => String(teacher?.Status || '').trim().toLowerCase() === 'active')
                    .filter((teacher) => {
                        if (instrumentFilters.length === 0) return true
                        return teacher?.InstrumentList?.some((instrument) => {
                            const normalized = normalizeInstrument(instrument)
                            return normalized.key && instrumentFilters.some((target) => target.key === normalized.key)
                        })
                    })

                const familyAddress = familyAddressLabel
                const [familyLocation, teacherLocations] = await Promise.all([
                    geocodeAddress(familyAddress),
                    Promise.all(
                        filteredTeachers.map(async (teacher) => {
                            const teacherAddress = [
                                teacher?.Address?.StreetAddress,
                                teacher?.Address?.City,
                                teacher?.Address?.State
                            ]
                                .filter(Boolean)
                                .join(', ')

                            const location = await geocodeAddress(teacherAddress)
                            if (!isValidLatLng(location)) return null

                            return {
                                id: `teacher-${teacher.TeacherID}`,
                                type: 'teacher',
                                teacherId: teacher.TeacherID,
                                name: teacher.Name,
                                address: teacherAddress,
                                instruments: getTeacherInstruments(teacher),
                                teacherData: teacher,
                                location
                            }
                        })
                    )
                ])

                const nextMarkers = []

                if (isValidLatLng(familyLocation)) {
                    nextMarkers.push({
                        id: `family-${modalInfo?.familyId || modalInfo?.todoId || 'family'}`,
                        type: 'family',
                        name: familyProfile?.familyName || modalInfo?.todoName || 'Family',
                        address: familyAddressLabel,
                        students: Array.isArray(modalInfo?.studentList) ? modalInfo.studentList.length : 0,
                        location: familyLocation
                    })
                }

                teacherLocations.filter(Boolean).forEach((marker) => nextMarkers.push(marker))

                if (!isActive) return

                setMarkers(nextMarkers)
                if (nextMarkers.length === 0) {
                    setError('No mappable addresses were found for this family and teacher group.')
                }
            } catch (err) {
                console.log(err)
                if (!isActive) return
                setError('Unable to load teacher map data right now.')
            } finally {
                if (isActive) {
                    setLoading(false)
                }
            }
        }

        loadMapData()

        return () => {
            isActive = false
        }
    }, [familyAddressLabel, familyProfile?.familyName, geocodeAddress, instrumentFilters, mapsReady, modalInfo?.familyId, modalInfo?.studentList, modalInfo?.todoId, modalInfo?.todoName, schoolFilter])

    const pinnedTeacherMarker = React.useMemo(
        () => markers.find((marker) => marker.id === pinnedTeacherId && marker.type === 'teacher') || null,
        [markers, pinnedTeacherId]
    )

    React.useEffect(() => {
        if (!mapsReady || !pinnedTeacherMarker?.teacherData) {
            setSelectedTeacherStudents([])
            return undefined
        }

        let isActive = true

        const loadSelectedTeacherStudents = async () => {
            setSelectedTeacherLoading(true)
            try {
                const teacher = pinnedTeacherMarker.teacherData
                const calendarResponse = await axios.get(
                    `${baseUrl}/calendar/teacher/${teacher.TeacherID}`,
                    getRequestHeaders()
                )
                const rawTeacherEvents = Array.isArray(calendarResponse?.data) ? calendarResponse.data : []
                const displayedDays = Array.from({ length: 61 }, (_, index) => addDays(new Date(), index))
                let expandedCalendarObject = {}

                createAndOrganizeTeachersCalendarEventsForDisplayedPeriodOfTime(
                    displayedDays,
                    rawTeacherEvents.filter((event) => !event?.isPendingOffer),
                    (result) => {
                        expandedCalendarObject = result || {}
                    }
                )

                const teacherEvents = displayedDays
                    .flatMap((day) => expandedCalendarObject?.[format(day, 'yyyy-MM-dd')]?.events || [])
                    .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime))

                const familiesFromEvents = {}

                teacherEvents.forEach((event) => {
                    const familyId = String(event?.familyId || '')
                    const address = event?.familyAddress || {}
                    const addressLine = address?.addressLine1
                    if (!familyId || !addressLine) return

                    if (!familiesFromEvents[familyId]) {
                        familiesFromEvents[familyId] = {
                            familyId,
                            familyName: event?.familyName || 'Family',
                            address: {
                                addressLine1: address?.addressLine1 || '',
                                addressLine2: address?.addressLine2 || '',
                                city: address?.city || '',
                                state: address?.state || '',
                                zipcode: address?.zipcode || ''
                            },
                            studentsById: {}
                        }
                    }

                    const studentIdKey = String(event?.studentId || event?.studentName || Math.random())
                    if (!familiesFromEvents[familyId].studentsById[studentIdKey]) {
                        familiesFromEvents[familyId].studentsById[studentIdKey] = {
                            id: studentIdKey,
                            studentName: getStudentDisplayName(event),
                            lessonLength: event?.lessonLength || '',
                            lessonSlots: new Set()
                        }
                    }

                    const slotLabel = getLessonSlotLabel(event)
                    if (slotLabel) {
                        familiesFromEvents[familyId].studentsById[studentIdKey].lessonSlots.add(slotLabel)
                    }
                })

                const familyMarkers = await Promise.all(
                    Object.values(familiesFromEvents).map(async (familyEntry) => {
                        const addressLabel = [
                            familyEntry?.address?.addressLine1,
                            familyEntry?.address?.addressLine2,
                            [familyEntry?.address?.city, familyEntry?.address?.state].filter(Boolean).join(', '),
                            familyEntry?.address?.zipcode
                        ]
                            .filter(Boolean)
                            .join(' ')
                            .trim()

                        const location = await geocodeAddress(addressLabel)
                        if (!isValidLatLng(location)) return null

                        const familyStudents = Object.values(familyEntry.studentsById).map((student) => ({
                            ...student,
                            lessonSlots: Array.from(student.lessonSlots.values()).sort()
                        }))

                        return {
                            id: `family-students-${familyEntry.familyId}`,
                            type: 'student-family',
                            familyId: familyEntry.familyId,
                            name: `${familyEntry.familyName} Family`,
                            address: addressLabel,
                            location,
                            students: familyStudents
                        }
                    })
                )

                if (!isActive) return

                setSelectedTeacherStudents(
                    familyMarkers
                        .filter(Boolean)
                        .flatMap((marker) =>
                            (marker.students || []).map((student) => ({
                                ...student,
                                familyId: marker.familyId,
                                familyName: marker.name
                            }))
                        )
                )

                setMarkers((prevMarkers) => {
                    const nonStudentMarkers = prevMarkers.filter((marker) => marker.type !== 'student-family')
                    return [...nonStudentMarkers, ...familyMarkers.filter(Boolean)]
                })
            } catch (err) {
                console.log(err)
                if (!isActive) return
                setSelectedTeacherStudents([])
            } finally {
                if (isActive) {
                    setSelectedTeacherLoading(false)
                }
            }
        }

        loadSelectedTeacherStudents()

        return () => {
            isActive = false
        }
    }, [geocodeAddress, mapsReady, pinnedTeacherMarker])

    React.useEffect(() => {
        if (pinnedTeacherMarker) return
        setSelectedTeacherStudents([])
        setSelectedTeacherLoading(false)
        setMarkers((prevMarkers) => prevMarkers.filter((marker) => marker.type !== 'student-family'))
        setSelectedMarkerId((currentId) => (String(currentId || '').startsWith('family-students-') ? null : currentId))
    }, [pinnedTeacherMarker])

    const mapCenter = React.useMemo(() => {
        const familyMarker = markers.find((marker) => marker.type === 'family' && isValidLatLng(marker.location))
        if (familyMarker) return familyMarker.location
        const studentFamilyMarker = markers.find((marker) => marker.type === 'student-family' && isValidLatLng(marker.location))
        if (studentFamilyMarker) return studentFamilyMarker.location
        const firstMarker = markers.find((marker) => isValidLatLng(marker.location))
        return firstMarker?.location || DEFAULT_MAP_CENTER
    }, [markers])

    const selectedMarker = React.useMemo(
        () => markers.find((marker) => marker.id === selectedMarkerId) || null,
        [markers, selectedMarkerId]
    )

    return (
        <div className='teacher-map-popup__overlay'>
            <div className='teacher-map-popup__modal'>
                <div className='teacher-map-popup__header'>
                    <div>
                        <div className='teacher-map-popup__eyebrow'>Teacher Coverage Map</div>
                        <div className='teacher-map-popup__title'>Family and teacher locations</div>
                        <div className='teacher-map-popup__subtitle'>
                            {instrumentFilters.length > 0
                                ? `Showing active ${modalInfo?.whichSchool?.toUpperCase()} teachers for ${instrumentFilters.map((inst) => inst.label).join(', ')}.`
                                : `Showing active ${modalInfo?.whichSchool?.toUpperCase()} teachers.`}
                        </div>
                    </div>
                    <button type='button' className='teacher-map-popup__close' onClick={onClose}>
                        <MdClose size={20} />
                    </button>
                </div>

                <div className='teacher-map-popup__content'>
                    <div className={`teacher-map-popup__sidebar ${pinnedTeacherMarker ? 'teacher-map-popup__sidebar--expanded' : ''}`}>
                        <div className='teacher-map-popup__legend-card'>
                            <div className='teacher-map-popup__legend-row'>
                                <span className='teacher-map-popup__legend-dot teacher-map-popup__legend-dot--family'></span>
                                Family
                            </div>
                            <div className='teacher-map-popup__legend-row'>
                                <span className='teacher-map-popup__legend-dot teacher-map-popup__legend-dot--teacher'></span>
                                Matching teachers
                            </div>
                            {pinnedTeacherMarker && (
                                <div className='teacher-map-popup__legend-row'>
                                    <span className='teacher-map-popup__legend-dot teacher-map-popup__legend-dot--student'></span>
                                    Active students
                                </div>
                            )}
                        </div>

                        <div className='teacher-map-popup__sidebar-columns'>
                        <div className='teacher-map-popup__list-card'>
                            <div className='teacher-map-popup__list-title'>Teachers on map</div>
                            <div className='teacher-map-popup__list-count'>
                                {pinnedTeacherMarker ? 'Selected teacher' : `${markers.filter((marker) => marker.type === 'teacher').length} teachers`}
                            </div>
                            <div className='teacher-map-popup__list'>
                                {markers
                                    .filter((marker) => marker.type === 'teacher')
                                    .filter((marker) => !pinnedTeacherMarker || marker.id === pinnedTeacherMarker.id)
                                    .map((marker) => (
                                        <button
                                            key={marker.id}
                                            type='button'
                                            className={`teacher-map-popup__teacher-row ${pinnedTeacherId === marker.id ? 'teacher-map-popup__teacher-row--selected' : ''}`}
                                            onClick={() => {
                                                setPinnedTeacherId((currentId) => {
                                                    const nextId = currentId === marker.id ? null : marker.id
                                                    setSelectedMarkerId(null)
                                                    return nextId
                                                })
                                            }}
                                            onMouseEnter={() => setSelectedMarkerId(marker.id)}
                                            onMouseLeave={() =>
                                                setSelectedMarkerId((currentId) => {
                                                    if (pinnedTeacherId === marker.id) {
                                                        return marker.id
                                                    }
                                                    return currentId === marker.id ? null : currentId
                                                })
                                            }>
                                            <MdPersonPinCircle size={18} />
                                            <div className='teacher-map-popup__teacher-row-copy'>
                                                <div className='teacher-map-popup__teacher-row-name'>{marker.name}</div>
                                                <div className='teacher-map-popup__teacher-row-detail'>
                                                    {marker.instruments.join(', ') || 'No instruments listed'}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                {markers.filter((marker) => marker.type === 'teacher').length === 0 && !loading && (
                                    <div className='teacher-map-popup__empty-copy'>No matching teachers found.</div>
                                )}
                            </div>
                        </div>
                        {pinnedTeacherMarker && (
                            <div className='teacher-map-popup__list-card teacher-map-popup__list-card--students'>
                                <div className='teacher-map-popup__list-title'>Students on map</div>
                                <div className='teacher-map-popup__list-count'>
                                    {selectedTeacherLoading ? 'Loading current roster...' : `${selectedTeacherStudents.length} active students`}
                                </div>
                                <div className='teacher-map-popup__list'>
                                    {selectedTeacherStudents.map((student) => (
                                        <button
                                            key={student.id}
                                            type='button'
                                            className='teacher-map-popup__student-row'
                                            onMouseEnter={() => setSelectedMarkerId(`family-students-${student.familyId}`)}
                                            onMouseLeave={() =>
                                                setSelectedMarkerId((currentId) =>
                                                    currentId === `family-students-${student.familyId}` ? pinnedTeacherMarker.id : currentId
                                                )
                                            }
                                            onClick={() => setSelectedMarkerId(`family-students-${student.familyId}`)}>
                                            <div className='teacher-map-popup__student-row-name'>{student.studentName}</div>
                                            <div className='teacher-map-popup__student-row-family'>{student.familyName}</div>
                                            <div className='teacher-map-popup__student-row-time'>
                                                {student.lessonSlots.length > 0 ? student.lessonSlots.join(' • ') : 'Schedule not found'}
                                            </div>
                                        </button>
                                    ))}
                                    {!selectedTeacherLoading && selectedTeacherStudents.length === 0 && (
                                        <div className='teacher-map-popup__empty-copy'>No active students found for this teacher.</div>
                                    )}
                                </div>
                            </div>
                        )}
                        </div>
                    </div>

                    <div className='teacher-map-popup__map-shell'>
                        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                            {mapsReady && !loading && !error && (
                                <GoogleMap
                                    style={{ width: '100%', height: '100%' }}
                                    zoom={11}
                                    center={mapCenter}
                                    gestureHandling='greedy'
                                    disableDefaultUI={false}
                                    mapId={GOOGLE_MAP_ID}>
                                    {markers
                                        .filter((marker) => isValidLatLng(marker.location))
                                        .map((marker) => (
                                            <AdvancedMarker
                                                key={marker.id}
                                                position={marker.location}
                                                onClick={() => setSelectedMarkerId(marker.id)}
                                                onMouseEnter={() => {
                                                    if (marker.type === 'student-family') {
                                                        setSelectedMarkerId(marker.id)
                                                    }
                                                }}
                                                onMouseLeave={() => {
                                                    if (marker.type === 'student-family') {
                                                        setSelectedMarkerId((currentId) =>
                                                            currentId === marker.id ? null : currentId
                                                        )
                                                    }
                                                }}>
                                                <Pin
                                                    background={
                                                        marker.type === 'family'
                                                            ? '#2563eb'
                                                            : marker.type === 'student-family'
                                                                ? '#22c55e'
                                                                : '#f97316'
                                                    }
                                                    borderColor={
                                                        marker.type === 'family'
                                                            ? '#1d4ed8'
                                                            : marker.type === 'student-family'
                                                                ? '#16a34a'
                                                                : '#ea580c'
                                                    }
                                                    glyphColor='#ffffff'
                                                />
                                            </AdvancedMarker>
                                        ))}
                                    {selectedMarker && isValidLatLng(selectedMarker.location) && (
                                        <InfoWindow
                                            position={selectedMarker.location}
                                            onCloseClick={() => setSelectedMarkerId(null)}>
                                            <div className='teacher-map-popup__info-window'>
                                                <div className='teacher-map-popup__info-header'>
                                                    <div className='teacher-map-popup__info-title'>{selectedMarker.name}</div>
                                                    <button
                                                        type='button'
                                                        className='teacher-map-popup__info-close'
                                                        onClick={() => setSelectedMarkerId(null)}
                                                    >
                                                        <MdClose size={16} />
                                                    </button>
                                                </div>
                                                <div className='teacher-map-popup__info-address'>
                                                    {selectedMarker.address}
                                                </div>
                                                {selectedMarker.type === 'teacher' && (
                                                    <div className='teacher-map-popup__info-pill-row'>
                                                        {(selectedMarker.instruments || []).slice(0, 3).map((instrument) => (
                                                            <div key={instrument} className='teacher-map-popup__info-pill'>
                                                                {instrument}
                                                            </div>
                                                        ))}
                                                        {(!selectedMarker.instruments || selectedMarker.instruments.length === 0) && (
                                                            <div className='teacher-map-popup__info-pill teacher-map-popup__info-pill--muted'>
                                                                No instruments listed
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {selectedMarker.type === 'student-family' && (
                                                    <div className='teacher-map-popup__info-section'>
                                                        <div className='teacher-map-popup__info-section-title'>Students at this stop</div>
                                                    <div className='teacher-map-popup__info-students'>
                                                        {(selectedMarker.students || []).map((student) => (
                                                            <div key={student.id} className='teacher-map-popup__info-student-row'>
                                                                <div className='teacher-map-popup__info-student-row-top'>
                                                                    <div className='teacher-map-popup__info-student-name'>{student.studentName}</div>
                                                                </div>
                                                                <div className='teacher-map-popup__info-student-time'>
                                                                    {student.lessonSlots.length > 0 ? student.lessonSlots.join(' • ') : 'Schedule not found'}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    </div>
                                                )}
                                            </div>
                                        </InfoWindow>
                                    )}
                                </GoogleMap>
                            )}
                        </APIProvider>
                        {!mapsReady || loading ? (
                            <div className='teacher-map-popup__loading teacher-map-popup__loading--overlay'>
                                <div className='teacher-map-popup__loading-title'>Loading map</div>
                                <div className='teacher-map-popup__loading-subtitle'>
                                    {!mapsReady
                                        ? 'Initializing Google Maps for this family.'
                                        : 'Geocoding the family address and matching teacher locations.'}
                                </div>
                            </div>
                        ) : null}
                        {mapsReady && !loading && error ? (
                            <div className='teacher-map-popup__error teacher-map-popup__error--overlay'>{error}</div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TeacherMapPopup
